/**
 * Session Settings Panel
 * Compact panel for viewing/editing session voting and offline settings.
 * Owner can edit settings where canEdit* flags are true; others see read-only.
 * Settings come from votingSettings embedded in session responses.
 */

import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Slider,
  Button,
  Alert,
} from '@mui/material'
import { Save as SaveIcon } from '@mui/icons-material'
import {
  VotingVisibility,
  VoteChangePolicy,
  VotingCompletionMode,
  VotingTieResolution,
  OfflineMemberAction,
  type SessionVotingSettings,
  type UpdateSessionSettingsRequest,
} from '@api/types/session'
import { useUpdateSessionSettings } from '../../hooks/useSession'
import { useSessionStore } from '../../stores/sessionStore'

interface SessionSettingsPanelProps {
  sessionId: string
  isOwner: boolean
  settings: SessionVotingSettings | null
}

const VOTING_VISIBILITY_LABELS: Record<number, string> = {
  [VotingVisibility.HiddenUntilCompleted]: 'Hidden Until Completed',
  [VotingVisibility.RealTime]: 'Real-Time',
}

const VOTE_CHANGE_LABELS: Record<number, string> = {
  [VoteChangePolicy.LockedAfterSubmission]: 'Locked After Submission',
  [VoteChangePolicy.LimitedWindow]: 'Limited Window',
  [VoteChangePolicy.AllowedUntilClose]: 'Allowed Until Close',
}

const VOTING_COMPLETION_LABELS: Record<number, string> = {
  [VotingCompletionMode.VotingPercentage]: 'Voting Percentage',
  [VotingCompletionMode.TimerBased]: 'Timer Based',
}

const VOTING_TIE_LABELS: Record<number, string> = {
  [VotingTieResolution.TimeoutRandomSelection]: 'Timeout + Random Selection',
  [VotingTieResolution.RevoteOnTiedOptions]: 'Revote on Tied Options',
}

const OFFLINE_MEMBER_LABELS: Record<number, string> = {
  [OfflineMemberAction.Pause]: 'Pause',
  [OfflineMemberAction.Continue]: 'Continue',
  [OfflineMemberAction.Abandon]: 'Abandon',
  [OfflineMemberAction.VoteOnAction]: 'Vote on Action',
}

export default function SessionSettingsPanel({ sessionId, isOwner, settings }: SessionSettingsPanelProps) {
  const updateSettings = useUpdateSessionSettings(sessionId)
  const store = useSessionStore()

  const [local, setLocal] = useState<UpdateSessionSettingsRequest>({})
  const [hasChanges, setHasChanges] = useState(false)

  // Reset local state when settings change
  useEffect(() => {
    if (settings) {
      setLocal({
        votingVisibility: settings.votingVisibility,
        voteChangePolicy: settings.voteChangePolicy,
        voteChangeWindowSeconds: settings.voteChangeWindowSeconds,
        votingCompletionMode: settings.votingCompletionMode,
        votingCompletionPercentage: settings.votingCompletionPercentage,
        votingCompletionTimerSeconds: settings.votingCompletionTimerSeconds,
        votingTieResolution: settings.votingTieResolution,
        votingTieTimeoutSeconds: settings.votingTieTimeoutSeconds,
        offlineMemberAction: settings.offlineMemberAction,
      })
      setHasChanges(false)
    }
  }, [settings])

  const handleChange = (field: keyof UpdateSessionSettingsRequest, value: number | null) => {
    setLocal(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync(local)
      // Optimistically update store since /state endpoint may not return votingSettings
      if (settings) {
        store.updateSessionState({
          votingSettings: { ...settings, ...local },
        } as any)
      }
      setHasChanges(false)
    } catch {
      // Error handled by mutation
    }
  }

  const hasEditableFields = useMemo(() => {
    if (!settings) return false
    return settings.canEditVotingVisibility ||
      settings.canEditVoteChangePolicy ||
      settings.canEditVotingCompletionMode ||
      settings.canEditVotingTieResolution ||
      settings.canEditOfflineMemberAction
  }, [settings])

  if (!settings) {
    return (
      <Box sx={{ px: 2, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No settings available
        </Typography>
      </Box>
    )
  }

  const canEdit = (flag: boolean) => isOwner && flag

  const renderSetting = (
    label: string,
    value: number,
    labels: Record<number, string>,
    editable: boolean,
    field: keyof UpdateSessionSettingsRequest,
  ) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {editable ? (
        <Select
          fullWidth
          size="small"
          value={local[field] ?? value}
          onChange={(e) => handleChange(field, e.target.value as number)}
          sx={{ mt: 0.5 }}
        >
          {Object.entries(labels).map(([val, lbl]) => (
            <MenuItem key={val} value={Number(val)}>{lbl}</MenuItem>
          ))}
        </Select>
      ) : (
        <Typography variant="body2">{labels[value] ?? 'Group Default'}</Typography>
      )}
    </Box>
  )

  return (
    <Box sx={{ px: 2, py: 1 }}>
      {renderSetting('Voting Visibility', settings.votingVisibility, VOTING_VISIBILITY_LABELS, canEdit(settings.canEditVotingVisibility), 'votingVisibility')}

      {renderSetting('Vote Change Policy', settings.voteChangePolicy, VOTE_CHANGE_LABELS, canEdit(settings.canEditVoteChangePolicy), 'voteChangePolicy')}

      {/* Conditional: vote change window when LimitedWindow */}
      {(local.voteChangePolicy ?? settings.voteChangePolicy) === VoteChangePolicy.LimitedWindow && (
        <Box sx={{ mb: 2, pl: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Change Window: {local.voteChangeWindowSeconds ?? settings.voteChangeWindowSeconds ?? 60}s
          </Typography>
          {canEdit(settings.canEditVoteChangePolicy) ? (
            <Slider
              size="small"
              min={25}
              max={900}
              value={local.voteChangeWindowSeconds ?? settings.voteChangeWindowSeconds ?? 60}
              onChange={(_, v) => handleChange('voteChangeWindowSeconds', v as number)}
            />
          ) : null}
        </Box>
      )}

      {renderSetting('Voting Completion', settings.votingCompletionMode, VOTING_COMPLETION_LABELS, canEdit(settings.canEditVotingCompletionMode), 'votingCompletionMode')}

      {/* Conditional: percentage when VotingPercentage */}
      {(local.votingCompletionMode ?? settings.votingCompletionMode) === VotingCompletionMode.VotingPercentage && (
        <Box sx={{ mb: 2, pl: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Required: {local.votingCompletionPercentage ?? settings.votingCompletionPercentage ?? 50}%
          </Typography>
          {canEdit(settings.canEditVotingCompletionMode) ? (
            <Slider
              size="small"
              min={25}
              max={100}
              value={local.votingCompletionPercentage ?? settings.votingCompletionPercentage ?? 50}
              onChange={(_, v) => handleChange('votingCompletionPercentage', v as number)}
            />
          ) : null}
        </Box>
      )}

      {/* Conditional: timer when TimerBased */}
      {(local.votingCompletionMode ?? settings.votingCompletionMode) === VotingCompletionMode.TimerBased && (
        <Box sx={{ mb: 2, pl: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Timer: {local.votingCompletionTimerSeconds ?? settings.votingCompletionTimerSeconds ?? 60}s
          </Typography>
          {canEdit(settings.canEditVotingCompletionMode) ? (
            <Slider
              size="small"
              min={25}
              max={900}
              value={local.votingCompletionTimerSeconds ?? settings.votingCompletionTimerSeconds ?? 60}
              onChange={(_, v) => handleChange('votingCompletionTimerSeconds', v as number)}
            />
          ) : null}
        </Box>
      )}

      {renderSetting('Tie Resolution', settings.votingTieResolution, VOTING_TIE_LABELS, canEdit(settings.canEditVotingTieResolution), 'votingTieResolution')}

      {/* Conditional: timeout when TimeoutRandomSelection */}
      {(local.votingTieResolution ?? settings.votingTieResolution) === VotingTieResolution.TimeoutRandomSelection && (
        <Box sx={{ mb: 2, pl: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Timeout: {local.votingTieTimeoutSeconds ?? settings.votingTieTimeoutSeconds ?? 60}s
          </Typography>
          {canEdit(settings.canEditVotingTieResolution) ? (
            <Slider
              size="small"
              min={25}
              max={900}
              value={local.votingTieTimeoutSeconds ?? settings.votingTieTimeoutSeconds ?? 60}
              onChange={(_, v) => handleChange('votingTieTimeoutSeconds', v as number)}
            />
          ) : null}
        </Box>
      )}

      {renderSetting('Offline Members', settings.offlineMemberAction, OFFLINE_MEMBER_LABELS, canEdit(settings.canEditOfflineMemberAction), 'offlineMemberAction')}

      {/* Read-only proximity settings */}
      {(settings.maxParticipantDistanceFeet != null || settings.participantProximityPercentage != null) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Proximity Requirements
          </Typography>
          {settings.maxParticipantDistanceFeet != null && (
            <Typography variant="body2">
              Max distance: {settings.maxParticipantDistanceFeet} ft
            </Typography>
          )}
          {settings.participantProximityPercentage != null && (
            <Typography variant="body2">
              Required proximity: {settings.participantProximityPercentage}%
            </Typography>
          )}
        </Box>
      )}

      {/* Save button */}
      {isOwner && hasEditableFields && hasChanges && (
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={updateSettings.isPending}
          fullWidth
          sx={{ mt: 1 }}
        >
          {updateSettings.isPending ? 'Saving...' : 'Save Settings'}
        </Button>
      )}

      {updateSettings.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Failed to save settings
        </Alert>
      )}
    </Box>
  )
}
